<script setup lang="ts">
    import { reactive, inject, onMounted } from 'vue';
    import { createNew, cloneSmsMessage } from '../TextMessages.access';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { UserEventService } from '@shared/Root/UserEvent.service';
    import { Permissions } from '@shared/Root/Permissions';
    import CloneSmsList from '../List/CloneSmsList.vue';
    import Loading from '@shared/components/Loading.vue';
    import { useRoute } from 'vue-router';

    const route = useRoute();
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const userEventService = injectorAccess.injector.get(UserEventService);

    const state = reactive<{
        isBusy: boolean;
        hasScope: boolean;
        eventId: string | undefined;
        segmentId: string | undefined;
        communicationTypeNumber: number;
    }>({
        isBusy: false,
        hasScope: false,
        eventId: undefined,
        segmentId: undefined,
        communicationTypeNumber: 0,
    });

    onMounted(async () => {
        const requireScopeParams: vRequireScopeParams = {
            Feature: 'Sms',
            Permission: Permissions.Write,
        };

        state.hasScope = scopeAccess.hasScope(requireScopeParams);

        state.eventId = route.query.eventId as string;
        state.segmentId = route.query.segmentId as string;

        if (route.query.communicationType) {
            state.communicationTypeNumber = parseInt(route.query.communicationType as string, 10);
        }
    });

    const onCreateNewTextMessage = async () => {
        if (state.isBusy || !state.hasScope) return;
        state.isBusy = true;

        try {
            const textMessage = await createNew(state.eventId, state.segmentId, state.communicationTypeNumber);
            userEventService.registerUserEvent('SMS', 'Created draft sms', '');
            document.location = '/TextMessages/' + textMessage.SmsMessageId;
        } catch (error) {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorCreatingIssueHeading, content: $Resources.ErrorCreatingIssueBody });
            state.isBusy = false;
        }
    };

    const onCopyTextMessage = async (textMessageId: string) => {
        if (state.isBusy || !state.hasScope) return;
        state.isBusy = true;

        try {
            const clonedSmsId = await cloneSmsMessage(textMessageId, state.eventId, state.segmentId, state.communicationTypeNumber);
            userEventService.registerUserEvent('SMS', 'Cloned sms', '');
            document.location = '/TextMessages/' + clonedSmsId;
        } catch (error) {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorCreatingIssueHeading, content: $Resources.ErrorCreatingIssueBody });
            state.isBusy = false;
        }
    };
</script>

<template>
    <header role="banner" class="p-lg-r p-lg-l">
        <div class="d-flex justify-content-between align-items-baseline">
            <h1>{{ $Resources.NewSmsFromTemplateHeading }}</h1>
        </div>

        <p class="m-lg-b">
            {{ $Resources.NewSmsFromTemplateIntroduction }}
        </p>
    </header>

    <CloneSmsList @clickedTextMessageId="onCopyTextMessage" @createNewTextMessage="onCreateNewTextMessage" :isBusy="state.isBusy" />
    <Loading :show="state.isBusy" :fixed="true" :loadingStyle="'progress-bar'" />
</template>
